import log from '../logger';

/**
 * The tenant's own content stylesheets, linked into the CMS document once an admin is signed
 * in.
 *
 * They are not decoration: Froala runs inline here (no `iframe: true` in
 * `wysiwygConfigDefaults`), so every `.fr-view` editing surface, in wysiwyg-manager and in the
 * directory entry editors, inherits its typography from this document. Without them an admin
 * composes in one typeface and the app renders another, with nothing to indicate why. See the
 * note in `css/simple-sidebar.css`.
 *
 * **Never before sign-in.** `/preview/asset` falls back to Basic auth when a request carries
 * no token, so a signed-out request for either file is answered 401 with
 * `WWW-Authenticate: Basic`, and a stylesheet that gets that answer makes the browser open its
 * own "Sign in" dialog. 1.1.17 wrote these links into index.html, and every tenant's login
 * page opened that dialog in prd (2026-10-05). The deleted `modules/root/root.html` never had
 * the problem because it rendered only inside the signed-in frame, which is where this is
 * called from, and only once the config names a signed-in user: a signed-out visitor to
 * `/main/` reaches the frame too, and gets a config with `loggedInUser: null`.
 *
 * **Where they go is load-bearing.** They are inserted at index.html's placeholder, which sits
 * before the bundle's stylesheet, so main.css and Tailwind still win a tie with the tenant's
 * CSS (`shared/ui/primitives/button.js`, `css/simple-sidebar.css`). Appended to the end of the
 * head instead, a tenant's bare `button { text-transform: uppercase }` would restyle the CMS.
 * With no placeholder they are not linked at all, for the same reason: an editor without the
 * tenant's fonts is the lesser fault.
 */
export const TENANT_STYLESHEETS = [
  '/preview/asset/cmsFonts.css?instanceName=sandbox',
  '/preview/asset/wysiwygPageStyles.css?instanceName=sandbox',
];

export const TENANT_STYLESHEETS_PLACEHOLDER_ID = 'tenant-stylesheets';

/** Safe to call again: the frame mounts after every sign-in, and nothing is linked twice. */
export function linkTenantStylesheets(doc = document) {
  const placeholder = doc.getElementById(TENANT_STYLESHEETS_PLACEHOLDER_ID);
  if (!placeholder) {
    log.error(
      `index.html has no #${TENANT_STYLESHEETS_PLACEHOLDER_ID}; the tenant stylesheets were not linked.`
    );
    return;
  }
  const linked = new Set(
    Array.from(doc.querySelectorAll('link[data-tenant-stylesheet]'), (link) =>
      link.getAttribute('href')
    )
  );
  TENANT_STYLESHEETS.filter((href) => !linked.has(href)).forEach((href) => {
    const link = doc.createElement('link');
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('href', href);
    link.dataset.tenantStylesheet = '';
    placeholder.parentNode.insertBefore(link, placeholder);
  });
}
